import type { ISetFilterParams } from 'ag-grid-community';

import type { ColDef } from '@/grid/types';

import type { AuditFeedItem } from '../api';
import { formatDate } from '../util/grid';
import { operationTypeNames } from '../util/types';

/** Short form of a record id for rows whose name can't be resolved. */
export function shortId(id: string): string {
  return id.split('-')[0];
}

export function changedFieldsLabel(fields: string[]): string {
  if (fields.length <= 3) return fields.join(', ');
  return `${fields.slice(0, 3).join(', ')} (+${fields.length - 3} more)`;
}

export const auditFeedColumnDefs: ColDef<AuditFeedItem>[] = [
  {
    field: 'operationTimestamp',
    headerName: 'Date',
    width: 200,
    valueFormatter: (p) => formatDate(p.value),
    // Sort is fixed server-side (newest first); the toolbar presets filter by date.
    filter: false,
    sortable: false,
  },
  {
    field: 'entityDisplayName',
    headerName: 'Entity',
    width: 180,
    // The entity is chosen in the toolbar so it can drive the server-side query.
    filter: false,
    sortable: false,
  },
  {
    colId: 'record',
    headerName: 'Record',
    width: 200,
    valueGetter: (p) =>
      p.data ? (p.data.recordName ?? shortId(p.data.objectId)) : '',
    filter: false,
    sortable: false,
  },
  {
    field: 'operationType',
    headerName: 'Operation Type',
    width: 170,
    valueFormatter: (p) =>
      operationTypeNames[p.value as keyof typeof operationTypeNames] || p.value,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['INSERT', 'UPDATE', 'DELETE'] as Array<
        keyof typeof operationTypeNames
      >,
      valueFormatter(params: { value: keyof typeof operationTypeNames }) {
        return operationTypeNames[params.value] || params.value;
      },
      defaultToNothingSelected: true,
      suppressSorting: true,
    } as ISetFilterParams,
    sortable: false,
  },
  {
    field: 'userEmail',
    headerName: 'User',
    width: 220,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      suppressAndOrCondition: true,
      buttons: ['reset', 'apply'],
      closeOnApply: true,
    },
    sortable: false,
  },
  {
    colId: 'changedFields',
    headerName: 'Changed Fields',
    flex: 1,
    minWidth: 240,
    valueGetter: (p) => changedFieldsLabel(p.data?.changedFields ?? []),
    tooltipValueGetter: (p) =>
      (p.data as AuditFeedItem | undefined)?.changedFields.join(', ') ?? '',
    filter: false,
    sortable: false,
  },
];
